// Modal universal de COMPRA — escolhe o que renderizar baseado em status:
//   - "pending"  → Pix copia-e-cola + QR + link público (fluxo de pagar)
//   - "paid"     → comprovante (verde, data + valor + identificador, imprimir)
//   - "expired"  → estado terminal informativo
//   - "canceled" → estado terminal informativo
//
// Antes, qualquer compra abria o fluxo de Pix — o que era confuso pra um
// recibo já pago. Agora ramifica.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Link2,
  Loader2,
  Mail,
  MessageCircle,
  Printer,
  Receipt,
  X,
  XCircle
} from "lucide-react";
import type { CompraCompanyBranding, CompraRow } from "./api";
import { api } from "./api";
import { PixDisplay } from "./CompraPacoteModal";
import { openAlert } from "./ConfirmPromptModal";

const open = signal<CompraRow | null>(null);

export function showCompraPendente(compra: CompraRow) {
  open.value = compra;
}

function formatBRL(cents: number): string {
  return (cents / 100).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL"
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return iso;
  }
}

export function CompraPendenteModal() {
  const compra = open.value;
  if (!compra) return null;

  function handleBackdrop(e: MouseEvent) {
    if (e.target === e.currentTarget) open.value = null;
  }

  const isPaid = compra.status === "paid";
  const isPending = compra.status === "pending";
  const title = isPaid
    ? "Comprovante de pagamento"
    : isPending
      ? "Pix pendente"
      : compra.status === "expired"
        ? "Cobrança expirada"
        : "Cobrança cancelada";

  return (
    <div
      class="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={handleBackdrop}
    >
      <div class="flex max-h-[92vh] w-full max-w-lg flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl">
        <header class="flex items-center justify-between gap-3 border-b border-border px-5 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            {isPaid ? (
              <Receipt className="size-4 text-emerald-600 dark:text-emerald-400" />
            ) : null}
            {title}
          </h2>
          <button
            type="button"
            onClick={() => (open.value = null)}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="flex-1 space-y-4 overflow-y-auto p-5">
          {isPaid && <PaidReceiptView compra={compra} />}
          {isPending && (
            <>
              <PixDisplay compra={compra} />
              <ChargeLinkBlock compraId={compra.id} />
            </>
          )}
          {(compra.status === "expired" || compra.status === "canceled") && (
            <TerminalStateView compra={compra} />
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Comprovante (status = paid)
// =============================================================================

function PaidReceiptView({ compra }: { compra: CompraRow }) {
  // Branding da org dona da disciplina/turma/offering — pra estampar logo +
  // cor primária no cabeçalho do recibo. Fetch on-mount (mesmo endpoint
  // getCompra agora devolve `company`).
  const [branding, setBranding] = useState<CompraCompanyBranding | null>(null);
  useEffect(() => {
    void api.getCompra(compra.id).then((r) => {
      if (r.company) setBranding(r.company);
    }).catch(() => { /* sem branding é ok — fallback genérico */ });
  }, [compra.id]);

  const accent = branding?.branding.colorPrimary || "#10b981"; // emerald-500 fallback

  return (
    <>
      <PrintStyles />
      <div class="space-y-4">
        <div class="print-receipt rounded-md border border-border bg-background p-5 print:border-0 print:p-0">
          {/* Cabeçalho com identidade da organização */}
          <header class="mb-4 flex items-center gap-3 border-b border-border pb-3">
            {branding?.branding.logoUrl ? (
              <img
                src={branding.branding.logoUrl}
                alt={branding.name}
                class="h-10 w-auto max-w-[140px] object-contain"
              />
            ) : (
              <div
                class="grid size-10 place-items-center rounded-md text-white font-bold"
                style={{ backgroundColor: accent }}
              >
                {(branding?.name ?? "C").charAt(0).toUpperCase()}
              </div>
            )}
            <div class="min-w-0 flex-1">
              <div class="text-sm font-bold">
                {branding?.name ?? "Comprovante"}
              </div>
              <div class="text-[10px] uppercase tracking-wider text-muted-foreground">
                Recibo de pagamento
              </div>
            </div>
          </header>

          {/* Faixa "pago" com a cor da org */}
          <div
            class="mb-4 flex flex-col items-center gap-1.5 rounded-md px-4 py-5 text-center"
            style={{
              backgroundColor: `${accent}1a`, // accent + 10% alpha
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: `${accent}55`
            }}
          >
            <span
              class="grid size-12 place-items-center rounded-full text-white shadow-lg"
              style={{ backgroundColor: accent }}
            >
              <CheckCircle2 className="size-7" strokeWidth={2.5} />
            </span>
            <div class="text-base font-bold" style={{ color: accent }}>
              Pagamento confirmado
            </div>
            <div class="text-[11px] text-muted-foreground">
              {formatDateTime(compra.paidAt)}
            </div>
          </div>

          <div class="divide-y divide-border rounded-md border border-border">
            <ReceiptRow label="Pacote" value={compra.pacoteNomeSnap} />
            <ReceiptRow
              label="Aulas"
              value={`${compra.numAulasSnap} aula${compra.numAulasSnap === 1 ? "" : "s"}`}
            />
            <ReceiptRow
              label="Valor pago"
              value={formatBRL(compra.valorCents)}
              strong
            />
            <ReceiptRow label="Forma de pagamento" value="Pix" />
            <ReceiptRow label="Identificador" value={compra.txid} mono />
            {compra.e2eId && (
              <ReceiptRow label="End-to-end ID" value={compra.e2eId} mono small />
            )}
            <ReceiptRow
              label="Criado em"
              value={formatDateTime(compra.createdAt)}
              small
            />
          </div>

          <p class="mt-3 text-[10px] text-muted-foreground">
            Esse comprovante reflete os dados registrados no momento do
            pagamento. Guarde o identificador acima caso precise reaver a
            transação.
          </p>
        </div>

        <div class="flex flex-wrap gap-2 print:hidden">
          <button
            type="button"
            onClick={() => window.print()}
            class="inline-flex items-center gap-1.5 rounded-md text-xs font-semibold text-white px-3 py-2 hover:brightness-110"
            style={{ backgroundColor: accent }}
          >
            <Printer className="size-3.5" />
            Imprimir / salvar PDF
          </button>
          <CopyTxidButton txid={compra.txid} />
        </div>
      </div>
    </>
  );
}

/** Esconde TUDO da página exceto `.print-receipt` quando o usuário aciona
 *  Imprimir. Resolve o bug das 3 páginas (cada uma com o overlay do modal e
 *  fundo do app). Também força margem mínima e cores fiéis ao na impressão. */
function PrintStyles() {
  return (
    <style>{`
      @media print {
        @page { margin: 1.2cm; size: auto; }
        html, body { background: white !important; }
        body * { visibility: hidden !important; }
        .print-receipt, .print-receipt * { visibility: visible !important; }
        .print-receipt {
          position: absolute !important;
          left: 0; top: 0; right: 0;
          margin: 0 !important;
          padding: 0 !important;
          box-shadow: none !important;
          border: 0 !important;
          background: white !important;
          color: #111 !important;
          width: 100% !important;
          max-width: 100% !important;
          page-break-after: avoid;
        }
        /* Preserva cores dos badges/ícones (sem isso o browser strip
           background-color e o ícone branco fica invisível em fundo branco). */
        .print-receipt, .print-receipt * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        /* Garante que o SVG dentro do círculo "Pago" renderize. */
        .print-receipt svg {
          display: inline-block !important;
        }
        .print-receipt img { max-height: 56px !important; }
      }
    `}</style>
  );
}

function ReceiptRow({
  label,
  value,
  strong,
  mono,
  small
}: {
  label: string;
  value: string;
  strong?: boolean;
  mono?: boolean;
  small?: boolean;
}) {
  return (
    <div class="flex items-baseline justify-between gap-3 px-3 py-2">
      <span class="text-[11px] uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <span
        class={`min-w-0 truncate text-right ${
          strong ? "text-base font-bold text-foreground" : "text-sm text-foreground"
        } ${mono ? "font-mono" : ""} ${small ? "text-[11px]" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

function CopyTxidButton({ txid }: { txid: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(txid);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-semibold hover:bg-accent"
    >
      {copied ? (
        <Check className="size-3.5 text-emerald-600" />
      ) : (
        <Copy className="size-3.5" />
      )}
      {copied ? "Copiado!" : "Copiar identificador"}
    </button>
  );
}

// =============================================================================
// Terminal (expirado / cancelado)
// =============================================================================

function TerminalStateView({ compra }: { compra: CompraRow }) {
  const isExpired = compra.status === "expired";
  return (
    <div class="space-y-4">
      <div class="flex flex-col items-center gap-2 rounded-md border border-muted-foreground/30 bg-muted/30 px-4 py-6 text-center">
        <span class="grid size-12 place-items-center rounded-full bg-muted text-muted-foreground">
          {isExpired ? (
            <AlertCircle className="size-7" />
          ) : (
            <XCircle className="size-7" />
          )}
        </span>
        <div class="text-base font-bold text-foreground">
          {isExpired ? "Esta cobrança expirou" : "Esta cobrança foi cancelada"}
        </div>
        <div class="text-xs text-muted-foreground">
          {isExpired
            ? "O Pix tinha um prazo de validade e não foi pago a tempo."
            : "A cobrança foi cancelada antes do pagamento."}
        </div>
      </div>
      <div class="divide-y divide-border rounded-md border border-border bg-background/60">
        <ReceiptRow label="Pacote" value={compra.pacoteNomeSnap} />
        <ReceiptRow label="Valor" value={formatBRL(compra.valorCents)} />
        <ReceiptRow
          label="Criada em"
          value={formatDateTime(compra.createdAt)}
          small
        />
        {compra.canceledAt && (
          <ReceiptRow
            label="Cancelada em"
            value={formatDateTime(compra.canceledAt)}
            small
          />
        )}
      </div>
      <p class="text-[11px] text-muted-foreground">
        Pra adquirir esse pacote, gere uma nova cobrança pela tela da disciplina.
      </p>
    </div>
  );
}

// =============================================================================
// Pendente — link público
// =============================================================================

function ChargeLinkBlock({ compraId }: { compraId: string }) {
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function ensureLink(): Promise<string | null> {
    if (linkUrl) return linkUrl;
    setBusy(true);
    try {
      const r = await api.getChargeLink(compraId);
      setLinkUrl(r.url);
      return r.url;
    } catch (e) {
      void openAlert({ title: "Erro", message: e instanceof Error ? e.message : String(e) });
      return null;
    } finally {
      setBusy(false);
    }
  }
  async function copy() {
    const link = await ensureLink();
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }
  async function openWindow() {
    const link = await ensureLink();
    if (link) window.open(link, "_blank", "noopener,noreferrer");
  }
  async function shareWhatsApp() {
    const link = await ensureLink();
    if (!link) return;
    const msg = `Vou compartilhar este link de pagamento da minha escola:\n\n${link}`;
    window.open(
      `https://wa.me/?text=${encodeURIComponent(msg)}`,
      "_blank",
      "noopener,noreferrer"
    );
  }
  async function shareEmail() {
    const link = await ensureLink();
    if (!link) return;
    const subject = "Link de pagamento";
    const body = `Link: ${link}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  }

  return (
    <section class="rounded-md border border-primary/30 bg-primary/5 p-3">
      <h3 class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary">
        <Link2 className="size-3.5" />
        Link público de pagamento
      </h3>
      {linkUrl ? (
        <div class="space-y-2">
          <div class="flex items-center gap-2 rounded-md border border-border bg-background/60 px-2 py-1.5">
            <code class="min-w-0 flex-1 truncate font-mono text-[11px]">
              {linkUrl}
            </code>
            <button
              type="button"
              onClick={copy}
              title="Copiar link"
              aria-label="Copiar link"
              class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              {copied ? (
                <Check className="size-3.5 text-emerald-600" />
              ) : (
                <Copy className="size-3.5" />
              )}
            </button>
            <button
              type="button"
              onClick={openWindow}
              title="Abrir link"
              aria-label="Abrir link"
              class="grid size-7 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ExternalLink className="size-3.5" />
            </button>
          </div>
          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={shareWhatsApp}
              class="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-400"
            >
              <MessageCircle className="size-3.5" /> WhatsApp
            </button>
            <button
              type="button"
              onClick={shareEmail}
              class="inline-flex items-center gap-1.5 rounded-md border border-blue-500/40 bg-blue-500/10 px-2.5 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-500/20 dark:text-blue-400"
            >
              <Mail className="size-3.5" /> E-mail
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={ensureLink}
          disabled={busy}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="size-3.5 animate-spin" />
          ) : (
            <Link2 className="size-3.5" />
          )}
          Gerar link público
        </button>
      )}
      <p class="mt-1 text-[10px] text-muted-foreground">
        Página de pagamento com identidade visual da sua escola.
      </p>
    </section>
  );
}
